package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.ijoin.data.api.AiApi
import com.ijoin.data.api.MediaApi
import com.ijoin.data.model.LegalDocument
import com.ijoin.data.model.LegalDocumentType
import com.ijoin.data.model.EventStatus
import com.ijoin.data.repository.LegalRepository
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.platform.*
import com.ijoin.ui.components.AiConsentDialog
import com.ijoin.ui.components.LegalDocumentDialog
import com.ijoin.ui.components.SingleDocumentCheckbox
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardCorner
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventDialog
import com.ijoin.ui.design.EventInfoCard
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionTitle
import com.ijoin.ui.design.EventSelectableCard
import com.ijoin.ui.design.EventSegmentButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.EventToggleCard
import com.ijoin.ui.design.EventWizardStepIndicator
import com.ijoin.ui.design.EventWizardTopBar
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventFilterChipBorder
import com.ijoin.ui.design.eventFilterChipColors
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.ui.design.eventSwitchColors
import com.ijoin.ui.test.TestTags
import com.ijoin.analytics.AnalyticsTracker
import com.ijoin.util.datetime.currentDateInTimeZone
import com.ijoin.util.datetime.currentDateTimeInTimeZone
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone

private const val DEFAULT_EVENT_START_TIME = "10:00"
private const val DEFAULT_EVENT_END_TIME = "12:00"

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateEventWizardScreen(
    eventId: String? = null,
    initialData: EventWizardData? = null,
    isPrefilling: Boolean = false,
    defaultCurrencyCode: String? = null,
    onBackClick: () -> Unit,
    onSaveDraft: (EventWizardData) -> Unit,
    onCreateEvent: (EventWizardData) -> Unit,
    onUpdateEvent: (String, EventWizardData) -> Unit = { _, _ -> },
    modifier: Modifier = Modifier
) {
    val strings = strings()
    val isEditing = eventId != null

    var currentStep by remember { mutableStateOf(1) }
    val totalSteps = 3

    // Step 1: Basic Info
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var mediaUrls by remember { mutableStateOf(listOf<String>()) }
    var mobileMediaUrls by remember { mutableStateOf(listOf<String>()) }
    var mediaFolderKey by remember { mutableStateOf(initialData?.mediaStorageFolder) }
    var mediaUploadInProgress by remember { mutableStateOf(false) }
    var mediaUploadError by remember { mutableStateOf<String?>(null) }
    var mobileMediaUploadInProgress by remember { mutableStateOf(false) }
    var mobileMediaUploadError by remember { mutableStateOf<String?>(null) }
    var maxCapacity by remember { mutableStateOf("0") }
    var ticketPrice by remember { mutableStateOf("0") }
    var selectedCurrency by remember {
        val defaultCurrency = defaultCurrencyCode?.let { code ->
            getAllCurrencies().find { it.code == code }
        } ?: getAllCurrencies().find { it.code == "CAD" }
        mutableStateOf(defaultCurrency ?: getLocalCurrency())
    }
    var hiddenInfo by remember { mutableStateOf("") }
    var enableGroupChat by remember { mutableStateOf(false) }
    // AI Landing page
    var useAiLanding by remember { mutableStateOf(false) }
    var landingDescription by remember { mutableStateOf("") }
    var enableLandingPage by remember { mutableStateOf(false) }

    // Step 2: Schedule
    var scheduleType by remember { mutableStateOf<ScheduleType?>(null) }
    var eventDate by remember { mutableStateOf<LocalDate?>(null) }
    var eventEndDate by remember { mutableStateOf<LocalDate?>(null) }
    var eventTime by remember { mutableStateOf("") }
    var eventEndTime by remember { mutableStateOf("") }
    var selectedTimeZoneId by remember { mutableStateOf(getLocalTimeZone().displayId) }
    val selectedTimeZone = remember(selectedTimeZoneId) { createTimeZone(selectedTimeZoneId) }
    var selectedDaysOfWeek by remember { mutableStateOf(setOf<Int>()) }
    var weeklyTime by remember { mutableStateOf("") }
    var weeklyEndTime by remember { mutableStateOf("") }
    var selectedDaysOfMonth by remember { mutableStateOf(setOf<Int>()) }
    var monthlyTime by remember { mutableStateOf("") }
    var slotDaysOfWeek by remember { mutableStateOf(setOf<Int>()) }
    var slotStartTime by remember { mutableStateOf("") }
    var slotEndTime by remember { mutableStateOf("") }
    // New slot booking fields
    var minSlotsPerBooking by remember { mutableStateOf("1") }
    var slotDurationMinutes by remember { mutableStateOf("30") }
    // Per-day slot configuration: Map<dayOfWeek, Pair<startTime, endTime>>
    var slotDayTimes by remember { mutableStateOf(mapOf<Int, Pair<String, String>>()) }

    // Step 3: Location
    var isOnline by remember { mutableStateOf(false) }
    var locationAddress by remember { mutableStateOf("") }
    var locationLatitude by remember { mutableStateOf<Double?>(null) }
    var locationLongitude by remember { mutableStateOf<Double?>(null) }
    var onlineLink by remember { mutableStateOf("") }
    var enableCarpool by remember { mutableStateOf(false) }
    var carpoolPaymentPerPerson by remember { mutableStateOf("") }
    var dressCode by remember { mutableStateOf("") }
    var requireContactInfo by remember { mutableStateOf(false) }

    var showDatePickerDialog by remember { mutableStateOf(false) }
    var showTimeZoneDialog by remember { mutableStateOf(false) }
    var showCurrencyDialog by remember { mutableStateOf(false) }
    var showSaveDraftDialog by remember { mutableStateOf(false) }
    // Field-level errors (step 1)
    var titleError by remember { mutableStateOf<String?>(null) }
    var descriptionError by remember { mutableStateOf<String?>(null) }
    var ticketPriceError by remember { mutableStateOf<String?>(null) }
    var wizardError by remember { mutableStateOf<String?>(null) }

    // Organizer terms state
    val legalRepository = remember { LegalRepository() }
    var hasAcceptedOrganizerTerms by remember { mutableStateOf<Boolean?>(null) }
    var organizerTermsAccepted by remember { mutableStateOf(false) }
    var showOrganizerTermsDialog by remember { mutableStateOf(false) }
    var organizerTermsDocument by remember { mutableStateOf<LegalDocument?>(null) }
    var isLoadingOrganizerTerms by remember { mutableStateOf(false) }
    var organizerTermsError by remember { mutableStateOf<String?>(null) }

    val mediaPicker = rememberMediaPicker()
    val mobileMediaPicker = rememberMediaPicker()
    val mediaApi = remember { MediaApi() }
    val mapPicker = remember { createMapPicker() }
    val availableCurrencies = remember { getAllCurrencies() }

    LaunchedEffect(Unit) {
        if (eventDate == null) {
            eventDate = getCurrentDate(selectedTimeZone)
            eventEndDate = eventDate
        }
        if (eventTime.isBlank()) {
            eventTime = DEFAULT_EVENT_START_TIME
        }
        if (eventEndTime.isBlank()) {
            eventEndTime = DEFAULT_EVENT_END_TIME
        }
        // Check if user has already accepted organizer terms
        legalRepository.canPublishEvent()
            .onSuccess { canPublish ->
                hasAcceptedOrganizerTerms = canPublish
                organizerTermsAccepted = canPublish
            }
            .onFailure {
                hasAcceptedOrganizerTerms = false
            }
    }

    LaunchedEffect(scheduleType, selectedTimeZoneId) {
        if (scheduleType == ScheduleType.ONE_TIME) {
            if (eventDate == null) {
                eventDate = getCurrentDate(selectedTimeZone)
            }
            if (eventEndDate == null) {
                eventEndDate = eventDate
            }
            if (eventTime.isBlank()) {
                eventTime = DEFAULT_EVENT_START_TIME
            }
            if (eventEndTime.isBlank()) {
                eventEndTime = DEFAULT_EVENT_END_TIME
            }
        }
    }

    LaunchedEffect(initialData) {
        initialData?.let { data ->
            currentStep = 1
            title = data.title
            description = data.description
            mediaUrls = data.mediaUrls
            mobileMediaUrls = data.mobileImageUrls
            mediaFolderKey = data.mediaStorageFolder
            maxCapacity = data.maxCapacity?.toString().orEmpty()
            ticketPrice = data.ticketPrice.toString()
            hiddenInfo = data.hiddenInfo
            enableGroupChat = data.enableGroupChat
            scheduleType = data.scheduleType
            eventDate = data.eventDate
            eventEndDate = data.eventEndDate
            eventTime = data.eventTime
            selectedTimeZoneId = data.timeZone.takeUnless { it.isBlank() } ?: getLocalTimeZone().displayId
            selectedDaysOfWeek = data.selectedDaysOfWeek
            weeklyTime = data.weeklyTime
            weeklyEndTime = data.weeklyEndTime
            selectedDaysOfMonth = data.selectedDaysOfMonth
            monthlyTime = data.monthlyTime
            slotDaysOfWeek = data.slotDaysOfWeek
            slotStartTime = data.slotStartTime
            slotEndTime = data.slotEndTime
            minSlotsPerBooking = data.minSlotsPerBooking?.toString() ?: minSlotsPerBooking
            slotDurationMinutes = data.slotDurationMinutes?.toString() ?: slotDurationMinutes
            slotDayTimes = data.slotDayTimes
            isOnline = data.isOnline
            locationAddress = data.locationAddress
            locationLatitude = data.locationLatitude
            locationLongitude = data.locationLongitude
            onlineLink = data.onlineLink
            enableCarpool = data.enableCarpool
            carpoolPaymentPerPerson = data.carpoolPaymentPerPerson?.toString().orEmpty()
            val targetCurrency = availableCurrencies.firstOrNull { it.code == data.currency }
            if (targetCurrency != null) {
                selectedCurrency = targetCurrency
            }
        }
    }

    LaunchedEffect(mediaPicker.selectedFiles.size) {
        val newFiles = mediaPicker.selectedFiles
        if (newFiles.isNotEmpty()) {
            mediaUploadInProgress = true
            mediaUploadError = null
            for (dataUrl in newFiles) {
                val mediaType = if (dataUrl.startsWith("data:video", ignoreCase = true)) "videos" else "photos"
                val result = mediaApi.uploadMedia(dataUrl, mediaFolderKey, mediaType)
                if (result.isSuccess) {
                    val response = result.getOrNull()
                    if (response != null) {
                        mediaFolderKey = response.folderKey
                        mediaUrls = mediaUrls + response.url
                    }
                } else {
                    mediaUploadError = result.exceptionOrNull()?.message ?: strings.eventSaveErrorMessage
                    break
                }
            }
            mediaUploadInProgress = false
        }
    }

    LaunchedEffect(mobileMediaPicker.selectedFiles.size) {
        val newFiles = mobileMediaPicker.selectedFiles
        if (newFiles.isNotEmpty()) {
            mobileMediaUploadInProgress = true
            mobileMediaUploadError = null
            val imageFiles = newFiles.filterNot { it.startsWith("data:video", ignoreCase = true) }
            for (dataUrl in imageFiles) {
                val result = mediaApi.uploadMedia(dataUrl, mediaFolderKey, "photos-mobile")
                if (result.isSuccess) {
                    val response = result.getOrNull()
                    if (response != null) {
                        mediaFolderKey = response.folderKey
                        mobileMediaUrls = mobileMediaUrls + response.url
                    }
                } else {
                    mobileMediaUploadError = result.exceptionOrNull()?.message ?: strings.eventSaveErrorMessage
                    break
                }
            }
            mobileMediaUploadInProgress = false
        }
    }

    val focusManager = LocalFocusManager.current

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(brush = eventBackgroundBrush())
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Top))
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            }
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
        ) {
            // Top Bar
            EventWizardTopBar(
                title = if (isEditing) strings.editEvent else strings.createEventWizard,
                backContentDescription = strings.back,
                onBackClick = onBackClick,
                showSaveDraft = true,
                saveDraftLabel = strings.saveDraft,
                onSaveDraftClick = { showSaveDraftDialog = true }
            )

            // Content Card
            EventSurfaceCard(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = (16.dp * 0.4f), vertical = 8.dp),
                cornerRadius = EventCardCorner,
                contentPadding = PaddingValues(0.dp),
                verticalArrangement = Arrangement.Top
            ) {
                Column(
                    modifier = Modifier.fillMaxSize()
                ) {
                    EventWizardStepIndicator(
                        currentStep = currentStep,
                        totalSteps = totalSteps,
                        modifier = Modifier.padding(16.dp)
                    )

                    // Content
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState())
                                .padding(horizontal = 16.dp, vertical = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            when (currentStep) {
                                1 -> Step1BasicInfo(
                                    title = title,
                                    onTitleChange = { title = it; titleError = null },
                                    description = description,
                                    onDescriptionChange = {
                                        description = it
                                        landingDescription = it
                                        descriptionError = null
                                    },
                                    mediaUrls = mediaUrls,
                                    mobileMediaUrls = mobileMediaUrls,
                                    mediaUploadInProgress = mediaUploadInProgress,
                                    mediaUploadError = mediaUploadError,
                                    onMediaAdd = { mediaPicker.pickFiles() },
                                    onMediaRemove = { index -> mediaUrls = mediaUrls.filterIndexed { i, _ -> i != index } },
                                    mobileMediaUploadInProgress = mobileMediaUploadInProgress,
                                    mobileMediaUploadError = mobileMediaUploadError,
                                    onMobileMediaAdd = { mobileMediaPicker.pickFiles() },
                                    onMobileMediaRemove = { index ->
                                        mobileMediaUrls = mobileMediaUrls.filterIndexed { i, _ -> i != index }
                                    },
                                    maxCapacity = maxCapacity,
                                    onMaxCapacityChange = { maxCapacity = it },
                                    ticketPrice = ticketPrice,
                                    onTicketPriceChange = { ticketPrice = it; ticketPriceError = null },
                                    selectedCurrency = selectedCurrency,
                                    onCurrencyClick = { showCurrencyDialog = true },
                                    hiddenInfo = hiddenInfo,
                                    onHiddenInfoChange = { hiddenInfo = it },
                                    titleError = titleError,
                                    descriptionError = descriptionError,
                                    ticketPriceError = ticketPriceError,
                                    enableGroupChat = enableGroupChat,
                                    onEnableGroupChatChange = { enableGroupChat = it },
                                    useAiLanding = useAiLanding,
                                    onUseAiLandingChange = { enabled ->
                                        if (enabled) {
                                            landingDescription = description
                                            enableLandingPage = landingDescription.isNotBlank()
                                        } else {
                                            description = landingDescription
                                        }
                                        useAiLanding = enabled
                                    },
                                    landingDescription = landingDescription,
                                    onLandingDescriptionChange = {
                                        landingDescription = it
                                        description = it
                                        // Auto-enable landing page when description is filled
                                        enableLandingPage = it.isNotBlank()
                                    },
                                    enableLandingPage = enableLandingPage,
                                    onEnableLandingPageChange = { enableLandingPage = it }
                                )
                                2 -> {
                                    val startDateSnapshot = eventDate
                                    val resolvedEndDateForUi = eventEndDate ?: startDateSnapshot
                                    Step2Schedule(
                                        scheduleType = scheduleType,
                                        onScheduleTypeChange = { newType ->
                                            scheduleType = newType
                                            // Initialize default times when schedule type is selected
                                            when (newType) {
                                                ScheduleType.WEEKLY -> {
                                                    if (weeklyTime.isBlank()) weeklyTime = "12:00"
                                                    if (weeklyEndTime.isBlank()) weeklyEndTime = "14:00"
                                                }
                                                ScheduleType.ONE_TIME -> {
                                                    if (eventTime.isBlank()) eventTime = "12:00"
                                                    if (eventEndTime.isBlank()) eventEndTime = "14:00"
                                                }
                                                ScheduleType.MONTHLY -> {
                                                    if (monthlyTime.isBlank()) monthlyTime = "12:00"
                                                }
                                                else -> {}
                                            }
                                        },
                                        eventDate = eventDate,
                                        onEventDateChange = { newDate ->
                                            eventDate = newDate
                                            val currentEnd = eventEndDate
                                            if (currentEnd == null || currentEnd < newDate) {
                                                eventEndDate = newDate
                                            }
                                        },
                                        eventEndDate = resolvedEndDateForUi,
                                        onEventEndDateChange = { newDate ->
                                            val start = eventDate
                                            eventEndDate = if (start != null && newDate < start) start else newDate
                                        },
                                        eventTime = eventTime,
                                        onEventTimeChange = { eventTime = it },
                                        eventEndTime = eventEndTime,
                                        onEventEndTimeChange = { eventEndTime = it },
                                    selectedTimeZone = selectedTimeZone,
                                    selectedTimeZoneLabel = selectedTimeZoneId,
                                    onTimeZoneClick = { showTimeZoneDialog = true },
                                    selectedDaysOfWeek = selectedDaysOfWeek,
                                    onDaysOfWeekChange = { selectedDaysOfWeek = it },
                                    weeklyTime = weeklyTime,
                                    onWeeklyTimeChange = { weeklyTime = it },
                                    weeklyEndTime = weeklyEndTime,
                                    onWeeklyEndTimeChange = { weeklyEndTime = it },
                                    selectedDaysOfMonth = selectedDaysOfMonth,
                                    onDaysOfMonthChange = { selectedDaysOfMonth = it },
                                    monthlyTime = monthlyTime,
                                    onMonthlyTimeChange = { monthlyTime = it },
                                    slotDaysOfWeek = slotDaysOfWeek,
                                    onSlotDaysOfWeekChange = { slotDaysOfWeek = it },
                                    slotStartTime = slotStartTime,
                                    onSlotStartTimeChange = { slotStartTime = it },
                                        slotEndTime = slotEndTime,
                                        onSlotEndTimeChange = { slotEndTime = it },
                                    minSlotsPerBooking = minSlotsPerBooking,
                                    onMinSlotsPerBookingChange = { minSlotsPerBooking = it },
                                    slotDurationMinutes = slotDurationMinutes,
                                    onSlotDurationMinutesChange = { slotDurationMinutes = it },
                                    slotDayTimes = slotDayTimes,
                                    onSlotDayTimesChange = { slotDayTimes = it }
                                    )
                                }
                                3 -> {
                                    val scope = rememberCoroutineScope()
                                    Column {
                                        Step3EventSetup(
                                            dressCode = dressCode,
                                            onDressCodeChange = { dressCode = it },
                                            locationAddress = locationAddress,
                                            onLocationAddressChange = { locationAddress = it },
                                            onSelectOnMapClick = {
                                                mapPicker.pickLocation { result ->
                                                    result?.let {
                                                        locationAddress = it.address
                                                        locationLatitude = it.latitude
                                                        locationLongitude = it.longitude
                                                    }
                                                }
                                            },
                                            enableCarpool = enableCarpool,
                                            onEnableCarpoolChange = { enableCarpool = it },
                                            carpoolPaymentPerPerson = carpoolPaymentPerPerson,
                                            onCarpoolPaymentChange = { carpoolPaymentPerPerson = it },
                                            currency = selectedCurrency,
                                            requireContactInfo = requireContactInfo,
                                            onRequireContactInfoChange = { requireContactInfo = it }
                                        )

                                        // Organizer terms checkbox (only show if not already accepted)
                                        if (hasAcceptedOrganizerTerms == false) {
                                            Spacer(modifier = Modifier.height(16.dp))
                                            SingleDocumentCheckbox(
                                                checked = organizerTermsAccepted,
                                                onCheckedChange = { checked ->
                                                    organizerTermsAccepted = checked
                                                    if (checked) {
                                                        // Accept organizer terms via API
                                                        scope.launch {
                                                            legalRepository.acceptOrganizerTerms()
                                                                .onSuccess {
                                                                    hasAcceptedOrganizerTerms = true
                                                                }
                                                                .onFailure {
                                                                    organizerTermsAccepted = false
                                                                }
                                                        }
                                                    }
                                                },
                                                onDocumentClick = {
                                                    showOrganizerTermsDialog = true
                                                    isLoadingOrganizerTerms = true
                                                    organizerTermsError = null
                                                    organizerTermsDocument = null
                                                    scope.launch {
                                                        legalRepository.getDocument(LegalDocumentType.ORGANIZER_TERMS)
                                                            .onSuccess { doc ->
                                                                organizerTermsDocument = doc
                                                                isLoadingOrganizerTerms = false
                                                            }
                                                            .onFailure { error ->
                                                                organizerTermsError = error.message ?: strings.legalDocumentLoadError
                                                                isLoadingOrganizerTerms = false
                                                            }
                                                    }
                                                },
                                                documentType = LegalDocumentType.ORGANIZER_TERMS
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Bottom buttons
                    WizardBottomBar(
                        currentStep = currentStep,
                        totalSteps = totalSteps,
                        onBackClick = { currentStep-- },
                        onNextClick = {
                            if (currentStep < totalSteps) {
                                // Step-wise validation
                                wizardError = null
                                if (currentStep == 1) {
                                    val errs = mutableListOf<Boolean>()
                                    if (title.isBlank()) { titleError = strings.missingTitle; errs += true }
                                    // Description is optional when AI landing mode is enabled
                                    if (!useAiLanding && description.isBlank()) { descriptionError = strings.missingDescription; errs += true }
                                    val priceVal = ticketPrice.toDoubleOrNull()
                                    if (priceVal != null && priceVal < 0.0) { ticketPriceError = strings.ticketPrice; errs += true }
                                    if (errs.isNotEmpty()) return@WizardBottomBar
                                }
                                if (currentStep == 2) {
                                    val errors = mutableListOf<String>()
                                    if (scheduleType == null) errors += strings.missingScheduleType
                                    when (scheduleType) {
                                        ScheduleType.ONE_TIME -> {
                                            val start = eventDate
                                            val end = eventEndDate ?: start
                                            if (start == null) errors += strings.missingDate
                                            if (eventTime.isBlank()) errors += strings.missingTime
                                            // Validate date is in the future
                                            if (start != null && eventTime.isNotBlank()) {
                                                val today = currentDateInTimeZone(selectedTimeZoneId)
                                                if (start < today) {
                                                    errors += strings.eventDateMustBeFuture
                                                } else if (start == today && !isTimeInFuture(eventTime, selectedTimeZoneId)) {
                                                    errors += strings.eventTimeMustBeFuture
                                                }
                                            }
                                            // Validate end date/time
                                            if (start != null && end != null) {
                                                if (end < start) {
                                                    errors += strings.invalidEndDate
                                                } else if (
                                                    end == start &&
                                                    eventTime.isNotBlank() &&
                                                    eventEndTime.isNotBlank() &&
                                                    !isStartTimeBeforeEndTime(eventTime, eventEndTime)
                                                ) {
                                                    errors += strings.invalidEndTime
                                                }
                                            }
                                        }
                                        ScheduleType.WEEKLY -> {
                                            if (selectedDaysOfWeek.isEmpty()) errors += strings.missingWeeklyDays
                                            if (weeklyTime.isBlank()) errors += strings.missingWeeklyTime
                                            if (
                                                weeklyTime.isNotBlank() &&
                                                weeklyEndTime.isNotBlank() &&
                                                !isStartTimeBeforeEndTime(weeklyTime, weeklyEndTime)
                                            ) {
                                                errors += strings.invalidEndTime
                                            }
                                        }
                                        ScheduleType.SLOT_BOOKING -> {
                                            if (slotDaysOfWeek.isEmpty()) errors += strings.missingSlotDays
                                            val hasGlobalTimes = slotStartTime.isNotBlank() && slotEndTime.isNotBlank()
                                            val hasPerDayTimes = slotDayTimes.values.any { (start, end) ->
                                                start.isNotBlank() && end.isNotBlank()
                                            }
                                            if (!hasGlobalTimes && !hasPerDayTimes) {
                                                // Only complain if neither global times nor any per-day times provided
                                                errors += strings.missingSlotStart
                                                errors += strings.missingSlotEnd
                                            }
                                            val hasInvalidGlobalTimes = hasGlobalTimes &&
                                                !isStartTimeBeforeEndTime(slotStartTime, slotEndTime)
                                            val hasInvalidPerDayTimes = slotDayTimes.values.any { (start, end) ->
                                                start.isNotBlank() && end.isNotBlank() && !isStartTimeBeforeEndTime(start, end)
                                            }
                                            if (hasInvalidGlobalTimes || hasInvalidPerDayTimes) {
                                                errors += strings.invalidEndTime
                                            }
                                        }
                                        else -> {}
                                    }
                                    if (errors.isNotEmpty()) { wizardError = errors.joinToString("\n"); return@WizardBottomBar }
                                }
                                currentStep++
                            } else {
                                // Detailed validation
                                wizardError = null
                                val errors = mutableListOf<String>()
                                if (title.isBlank()) errors += strings.missingTitle
                                // Description is optional when AI landing mode is enabled
                                if (!useAiLanding && description.isBlank()) errors += strings.missingDescription
                                if (scheduleType == null) errors += strings.missingScheduleType

                                when (scheduleType) {
                                    ScheduleType.ONE_TIME -> {
                                        val start = eventDate
                                        val end = eventEndDate ?: start
                                        if (start == null) errors += strings.missingDate
                                        if (eventTime.isBlank()) errors += strings.missingTime
                                        // Validate date is in the future
                                        if (start != null && eventTime.isNotBlank()) {
                                            val today = currentDateInTimeZone(selectedTimeZoneId)
                                            if (start < today) {
                                                errors += strings.eventDateMustBeFuture
                                            } else if (start == today && !isTimeInFuture(eventTime, selectedTimeZoneId)) {
                                                errors += strings.eventTimeMustBeFuture
                                            }
                                        }
                                        // Validate end date/time
                                        if (start != null && end != null) {
                                            if (end < start) {
                                                errors += strings.invalidEndDate
                                            } else if (
                                                end == start &&
                                                eventTime.isNotBlank() &&
                                                eventEndTime.isNotBlank() &&
                                                !isStartTimeBeforeEndTime(eventTime, eventEndTime)
                                            ) {
                                                errors += strings.invalidEndTime
                                            }
                                        }
                                    }
                                    ScheduleType.WEEKLY -> {
                                        if (selectedDaysOfWeek.isEmpty()) errors += strings.missingWeeklyDays
                                        if (weeklyTime.isBlank()) errors += strings.missingWeeklyTime
                                        if (
                                            weeklyTime.isNotBlank() &&
                                            weeklyEndTime.isNotBlank() &&
                                            !isStartTimeBeforeEndTime(weeklyTime, weeklyEndTime)
                                        ) {
                                            errors += strings.invalidEndTime
                                        }
                                    }
                                    ScheduleType.SLOT_BOOKING -> {
                                        if (slotDaysOfWeek.isEmpty()) errors += strings.missingSlotDays
                                        val hasGlobalTimes = slotStartTime.isNotBlank() && slotEndTime.isNotBlank()
                                        val hasPerDayTimes = slotDayTimes.values.any { (start, end) ->
                                            start.isNotBlank() && end.isNotBlank()
                                        }
                                        if (!hasGlobalTimes && !hasPerDayTimes) {
                                            errors += strings.missingSlotStart
                                            errors += strings.missingSlotEnd
                                        }
                                        val hasInvalidGlobalTimes = hasGlobalTimes &&
                                            !isStartTimeBeforeEndTime(slotStartTime, slotEndTime)
                                        val hasInvalidPerDayTimes = slotDayTimes.values.any { (start, end) ->
                                            start.isNotBlank() && end.isNotBlank() && !isStartTimeBeforeEndTime(start, end)
                                        }
                                        if (hasInvalidGlobalTimes || hasInvalidPerDayTimes) {
                                            errors += strings.invalidEndTime
                                        }
                                    }
                                    else -> {}
                                }

                                val priceVal = ticketPrice.toDoubleOrNull() ?: 0.0
                                if (priceVal < 0.0) errors += strings.ticketPrice

                                val locationOk = locationAddress.isNotBlank()
                                if (!locationOk) errors += strings.missingLocation

                                // Check organizer terms acceptance
                                if (!organizerTermsAccepted && hasAcceptedOrganizerTerms != true) {
                                    errors += strings.pleaseAcceptOrganizerTerms
                                }

                                if (errors.isNotEmpty()) {
                                    wizardError = errors.joinToString("\n")
                                    return@WizardBottomBar
                                }

                                // For ONE_TIME: use eventDate/eventEndDate and eventTime/eventEndTime
                                // For WEEKLY/MONTHLY: use weeklyTime/weeklyEndTime or monthlyTime, no specific dates
                                val (resolvedEventDate, resolvedEventEndDate, resolvedEventTime, resolvedEventEndTime) = when (scheduleType) {
                                    ScheduleType.ONE_TIME -> {
                                        val startSnapshot = eventDate
                                        val endSnapshot = eventEndDate ?: startSnapshot
                                        Tuple4(startSnapshot, endSnapshot, eventTime, eventEndTime)
                                    }
                                    ScheduleType.WEEKLY -> {
                                        val startSnapshot = eventDate
                                        val endSnapshot = eventEndDate ?: startSnapshot
                                        Tuple4(startSnapshot, endSnapshot, weeklyTime, weeklyEndTime)
                                    }
                                    ScheduleType.MONTHLY -> {
                                        val startSnapshot = eventDate
                                        val endSnapshot = eventEndDate ?: startSnapshot
                                        Tuple4(startSnapshot, endSnapshot, monthlyTime, "")
                                    }
                                    ScheduleType.SLOT_BOOKING, null -> {
                                        Tuple4(null, null, "", "")
                                    }
                                }
                                val payload = EventWizardData(
                                    title = title,
                                    description = description,
                                    mediaUrls = mediaUrls,
                                    mobileImageUrls = mobileMediaUrls,
                                    mediaStorageFolder = mediaFolderKey,
                                    status = EventStatus.PUBLISHED.name,
                                    maxCapacity = maxCapacity.toIntOrNull(),
                                    ticketPrice = ticketPrice.toDoubleOrNull() ?: 0.0,
                                    currency = selectedCurrency.code,
                                        hiddenInfo = hiddenInfo,
                                        scheduleType = scheduleType,
                                        eventDate = resolvedEventDate,
                                        eventEndDate = resolvedEventEndDate,
                                        eventTime = resolvedEventTime,
                                        eventEndTime = resolvedEventEndTime,
                                        timeZone = selectedTimeZoneId,
                                        selectedDaysOfWeek = selectedDaysOfWeek,
                                        weeklyTime = weeklyTime,
                                        weeklyEndTime = weeklyEndTime,
                                        selectedDaysOfMonth = selectedDaysOfMonth,
                                        monthlyTime = monthlyTime,
                                        slotDaysOfWeek = slotDaysOfWeek,
                                        slotStartTime = slotStartTime,
                                        slotEndTime = slotEndTime,
                                        minSlotsPerBooking = minSlotsPerBooking.toIntOrNull(),
                                        slotDurationMinutes = slotDurationMinutes.toIntOrNull(),
                                        slotDayTimes = slotDayTimes,
                                        isOnline = false,
                                        locationAddress = locationAddress,
                                        locationLatitude = locationLatitude,
                                        locationLongitude = locationLongitude,
                                        onlineLink = "",
                                        enableCarpool = enableCarpool,
                                        carpoolPaymentPerPerson = carpoolPaymentPerPerson.toDoubleOrNull(),
                                        enableGroupChat = enableGroupChat,
                                        requireContactInfo = requireContactInfo,
                                        useAiLanding = useAiLanding,
                                        landingDescription = landingDescription,
                                        enableLandingPage = enableLandingPage
                                    )
                                AnalyticsTracker.trackSchedule(
                                    eventId = eventId,
                                    eventType = scheduleType?.name,
                                    title = title
                                )
                                if (isEditing && eventId != null) {
                                    onUpdateEvent(eventId, payload)
                                } else {
                                    onCreateEvent(payload)
                                }
                            }
                        },
                        isEditing = isEditing
                    )
                    wizardError?.let {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = it,
                            color = MaterialTheme.colorScheme.error,
                            modifier = Modifier.testTag(TestTags.CreateEventWizard.WIZARD_ERROR_TEXT)
                        )
                    }
                }
            }
        }

        if (isPrefilling) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .background(EventCardSurface.copy(alpha = 0.8f)),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator()
            }
        }
    }

    // Dialogs
    if (showTimeZoneDialog) {
        TimeZoneSelectionDialog(
            currentTimeZoneId = selectedTimeZoneId,
            onTimeZoneSelected = { tzId ->
                selectedTimeZoneId = tzId
                showTimeZoneDialog = false
            },
            onDismiss = { showTimeZoneDialog = false }
        )
    }

    if (showCurrencyDialog) {
        CurrencySelectionDialog(
            currentCurrency = selectedCurrency,
            onCurrencySelected = { currency ->
                selectedCurrency = currency
                showCurrencyDialog = false
            },
            onDismiss = { showCurrencyDialog = false }
        )
    }

    if (showSaveDraftDialog) {
        EventDialog(
            onDismissRequest = { showSaveDraftDialog = false }
        ) {
            Text(
                text = strings.saveDraft,
                style = MaterialTheme.typography.titleMedium,
                color = EventCardAccentDark
            )
            Text(
                text = "${strings.draft} ${strings.saved}",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                EventTextAction(
                    text = strings.cancel,
                    onClick = { showSaveDraftDialog = false }
                )
                Spacer(modifier = Modifier.width(8.dp))
                EventTextAction(
                    text = strings.ok,
                    onClick = {
                        // For ONE_TIME: use eventDate/eventEndDate and eventTime/eventEndTime
                        // For WEEKLY/MONTHLY: use weeklyTime/weeklyEndTime or monthlyTime, no specific dates
                        val (resolvedEventDate, resolvedEventEndDate, resolvedEventTime, resolvedEventEndTime) = when (scheduleType) {
                            ScheduleType.ONE_TIME -> {
                                val startSnapshot = eventDate
                                val endSnapshot = eventEndDate ?: startSnapshot
                                Tuple4(startSnapshot, endSnapshot, eventTime, eventEndTime)
                            }
                            ScheduleType.WEEKLY -> {
                                Tuple4(null, null, weeklyTime, weeklyEndTime)
                            }
                            ScheduleType.MONTHLY -> {
                                Tuple4(null, null, monthlyTime, "")
                            }
                            ScheduleType.SLOT_BOOKING, null -> {
                                Tuple4(null, null, "", "")
                            }
                        }
                        val payload = EventWizardData(
                            title = title,
                            description = description,
                            mediaUrls = mediaUrls,
                            mobileImageUrls = mobileMediaUrls,
                            mediaStorageFolder = mediaFolderKey,
                            status = EventStatus.DRAFT.name,
                            maxCapacity = maxCapacity.toIntOrNull(),
                            ticketPrice = ticketPrice.toDoubleOrNull() ?: 0.0,
                            currency = selectedCurrency.code,
                            hiddenInfo = hiddenInfo,
                            scheduleType = scheduleType,
                            eventDate = resolvedEventDate,
                            eventEndDate = resolvedEventEndDate,
                            eventTime = resolvedEventTime,
                            eventEndTime = resolvedEventEndTime,
                            timeZone = selectedTimeZoneId,
                            selectedDaysOfWeek = selectedDaysOfWeek,
                            weeklyTime = weeklyTime,
                            weeklyEndTime = weeklyEndTime,
                            selectedDaysOfMonth = selectedDaysOfMonth,
                            monthlyTime = monthlyTime,
                            slotDaysOfWeek = slotDaysOfWeek,
                            slotStartTime = slotStartTime,
                            slotEndTime = slotEndTime,
                            minSlotsPerBooking = minSlotsPerBooking.toIntOrNull(),
                            slotDurationMinutes = slotDurationMinutes.toIntOrNull(),
                            slotDayTimes = slotDayTimes,
                            isOnline = false,
                            locationAddress = locationAddress,
                            locationLatitude = locationLatitude,
                            locationLongitude = locationLongitude,
                            onlineLink = "",
                            enableCarpool = enableCarpool,
                            carpoolPaymentPerPerson = carpoolPaymentPerPerson.toDoubleOrNull(),
                            enableGroupChat = enableGroupChat,
                            requireContactInfo = requireContactInfo,
                            useAiLanding = useAiLanding,
                            landingDescription = landingDescription,
                            enableLandingPage = enableLandingPage
                        )
                        if (isEditing && eventId != null) {
                            onUpdateEvent(eventId, payload)
                        } else {
                            onSaveDraft(payload)
                        }
                        showSaveDraftDialog = false
                    }
                )
            }
        }
    }

    // Organizer terms dialog
    if (showOrganizerTermsDialog) {
        LegalDocumentDialog(
            document = organizerTermsDocument,
            isLoading = isLoadingOrganizerTerms,
            error = organizerTermsError,
            onDismiss = {
                showOrganizerTermsDialog = false
                organizerTermsDocument = null
                organizerTermsError = null
            }
        )
    }
}

@Composable
private fun WizardBottomBar(
    currentStep: Int,
    totalSteps: Int,
    onBackClick: () -> Unit,
    onNextClick: () -> Unit,
    isEditing: Boolean = false
) {
    val strings = strings()
    val buttonHeight = 42.dp * 0.9f // Made 10% lower (42 -> 37.8)
    val additionalPadding = buttonHeight * 0.7f // 60% of button height (50% + 10%)
    val primaryButtonTag = if (currentStep < totalSteps) {
        TestTags.CreateEventWizard.NEXT_BUTTON
    } else {
        TestTags.CreateEventWizard.CREATE_BUTTON
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp)
            .padding(bottom = 16.dp + additionalPadding), // Original padding + 60% of button height
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        if (currentStep > 1) {
            EventOutlineButton(
                text = strings.previous,
                onClick = onBackClick,
                modifier = Modifier
                    .weight(1f)
                    .height(buttonHeight),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp)
            )
        }
        EventPrimaryButton(
            text = when {
                currentStep < totalSteps -> strings.next
                isEditing -> strings.save
                else -> strings.create
            },
            onClick = onNextClick,
            modifier = Modifier
                .weight(1f)
                .height(buttonHeight)
                .testTag(primaryButtonTag),
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp)
        )
    }
}

// Number Picker Component
@Composable
private fun NumberPicker(
    value: Int,
    onValueChange: (Int) -> Unit,
    range: IntRange,
    modifier: Modifier = Modifier,
    displayTransform: (Int) -> String = { it.toString().padStart(2, '0') },
    pickerTag: String? = null
) {
    val listState = rememberLazyListState(initialFirstVisibleItemIndex = value - range.first + 2)
    val coroutineScope = rememberCoroutineScope()
    val density = LocalDensity.current
    val itemHeightPx = remember(density) { with(density) { 40.dp.roundToPx() } }
    val totalItems = range.count() + 4
    val maxIndex = totalItems - 1
    val snapFlingBehavior = rememberSnapFlingBehavior(lazyListState = listState)

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress) {
            val index = listState.firstVisibleItemIndex
            val offset = listState.firstVisibleItemScrollOffset
            val targetIndex = if (offset > itemHeightPx / 2) index + 1 else index
            val targetValue = (targetIndex - 2 + range.first).coerceIn(range)
            val currentAlignedIndex = (value - range.first + 2).coerceIn(0, maxIndex)
            val needsValueUpdate = targetValue != value
            val needsSnap = offset != 0 || index != currentAlignedIndex

            if (needsValueUpdate) {
                onValueChange(targetValue)
            }
            if (needsValueUpdate || needsSnap) {
                coroutineScope.launch {
                    val snapIndex = (targetValue - range.first + 2).coerceIn(0, maxIndex)
                    listState.animateScrollToItem(snapIndex)
                }
            }
        }
    }

    Box(
        modifier = modifier
            .height(120.dp)
            .width(60.dp)
    ) {
        // Selection indicator
        Box(
            modifier = Modifier
                .align(Alignment.Center)
                .fillMaxWidth()
                .height(40.dp)
                .background(EventCardAccentSoft, RoundedCornerShape(EventCardInputCorner))
        )

        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .let { base -> pickerTag?.let { base.testTag(it) } ?: base },
            contentPadding = PaddingValues(vertical = 40.dp),
            flingBehavior = snapFlingBehavior
        ) {
            items(totalItems) { index ->
                val adjustedIndex = index - 2
                val itemValue = adjustedIndex + range.first
                val isSelected = itemValue == value
                val alpha = when {
                    itemValue < range.first || itemValue > range.last -> 0f
                    isSelected -> 1f
                    else -> 0.3f
                }

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(40.dp)
                        .alpha(alpha)
                        .clickable(enabled = itemValue in range) {
                            if (itemValue in range) {
                                onValueChange(itemValue)
                                coroutineScope.launch {
                                    val targetIndex = (itemValue - range.first + 2).coerceIn(0, maxIndex)
                                    listState.animateScrollToItem(targetIndex)
                                }
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    if (itemValue in range) {
                        Text(
                            text = displayTransform(itemValue),
                            style = MaterialTheme.typography.titleLarge.copy(
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                                color = if (isSelected) EventCardAccentDark else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        )
                    }
                }
            }
        }
    }
}

// Date Picker with wheels
@Composable
private fun WheelDatePicker(
    selectedDate: LocalDate,
    onDateChange: (LocalDate) -> Unit,
    modifier: Modifier = Modifier,
    strings: Strings,
    dayPickerTag: String? = null,
    monthPickerTag: String? = null,
    yearPickerTag: String? = null
) {
    val currentYear = selectedDate.year
    val currentMonth = selectedDate.monthNumber
    val currentDay = selectedDate.dayOfMonth

    val daysInMonth = remember(currentYear, currentMonth) {
        try {
            val daysPerMonth = intArrayOf(31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
            val isLeapYear = (currentYear % 4 == 0 && currentYear % 100 != 0) || (currentYear % 400 == 0)
            if (currentMonth == 2 && isLeapYear) 29 else daysPerMonth[currentMonth - 1]
        } catch (e: Exception) {
            31
        }
    }

    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(strings.day, style = MaterialTheme.typography.labelMedium)
            NumberPicker(
                value = currentDay,
                onValueChange = { newDay ->
                    val validDay = newDay.coerceIn(1, daysInMonth)
                    try {
                        onDateChange(LocalDate(currentYear, currentMonth, validDay))
                    } catch (e: Exception) {}
                },
                range = 1..daysInMonth,
                pickerTag = dayPickerTag
            )
        }

        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(strings.month, style = MaterialTheme.typography.labelMedium)
            NumberPicker(
                value = currentMonth,
                onValueChange = { newMonth ->
                    val newDaysInMonth = try {
                        val daysPerMonth = intArrayOf(31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
                        val isLeapYear = (currentYear % 4 == 0 && currentYear % 100 != 0) || (currentYear % 400 == 0)
                        if (newMonth == 2 && isLeapYear) 29 else daysPerMonth[newMonth - 1]
                    } catch (e: Exception) {
                        31
                    }
                    val validDay = currentDay.coerceIn(1, newDaysInMonth)
                    try {
                        onDateChange(LocalDate(currentYear, newMonth, validDay))
                    } catch (e: Exception) {}
                },
                range = 1..12,
                pickerTag = monthPickerTag
            )
        }

        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(strings.year, style = MaterialTheme.typography.labelMedium)
            NumberPicker(
                value = currentYear,
                onValueChange = { newYear ->
                    val newDaysInMonth = try {
                        val daysPerMonth = intArrayOf(31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
                        val isLeapYear = (newYear % 4 == 0 && newYear % 100 != 0) || (newYear % 400 == 0)
                        if (currentMonth == 2 && isLeapYear) 29 else daysPerMonth[currentMonth - 1]
                    } catch (e: Exception) {
                        31
                    }
                    val validDay = currentDay.coerceIn(1, newDaysInMonth)
                    try {
                        onDateChange(LocalDate(newYear, currentMonth, validDay))
                    } catch (e: Exception) {}
                },
                range = 2024..2030,
                displayTransform = { it.toString() },
                pickerTag = yearPickerTag
            )
        }
    }
}

// Helper function to compare times in HH:mm format
private fun parseTimeToMinutes(time: String): Int {
    val parts = time.split(":")
    if (parts.size != 2) return 0
    return parts[0].toIntOrNull()?.times(60)?.plus(parts[1].toIntOrNull() ?: 0) ?: 0
}

private fun isStartTimeBeforeEndTime(startTime: String, endTime: String): Boolean {
    return parseTimeToMinutes(startTime) < parseTimeToMinutes(endTime)
}

private fun addMinutesToTime(time: String, minutes: Int): String {
    val totalMinutes = parseTimeToMinutes(time) + minutes
    val hours = (totalMinutes / 60) % 24
    val mins = totalMinutes % 60
    return "${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}"
}

/**
 * Checks if event date is in the future (after today in the event's timezone)
 */
private fun isDateInFuture(date: LocalDate?, timeZoneId: String): Boolean {
    if (date == null) return false
    val today = currentDateInTimeZone(timeZoneId)
    return date > today
}

/**
 * Checks if event date is today in the event's timezone
 */
private fun isDateToday(date: LocalDate?, timeZoneId: String): Boolean {
    if (date == null) return false
    val today = currentDateInTimeZone(timeZoneId)
    return date == today
}

/**
 * Checks if event time is in the future when the event is today
 * Returns true if the time is after the current time in the event's timezone
 */
private fun isTimeInFuture(time: String, timeZoneId: String): Boolean {
    if (time.isBlank()) return false
    val now = currentDateTimeInTimeZone(timeZoneId)
    val eventMinutes = parseTimeToMinutes(time)
    val nowMinutes = now.hour * 60 + now.minute
    return eventMinutes > nowMinutes
}

/**
 * Validates that the event date/time is in the future
 * For today: time must be after current time
 * For future dates: any time is acceptable
 */
private fun isEventDateTimeInFuture(date: LocalDate?, time: String, timeZoneId: String): Boolean {
    if (date == null) return false
    return when {
        isDateInFuture(date, timeZoneId) -> true
        isDateToday(date, timeZoneId) -> isTimeInFuture(time, timeZoneId)
        else -> false // date is in the past
    }
}

// Time Picker with wheels
@Composable
private fun WheelTimePicker(
    time: String,
    onTimeChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    hourPickerTag: String? = null,
    minutePickerTag: String? = null
) {
    val (hour, minute) = remember(time) {
        val parts = time.split(":")
        if (parts.size == 2) {
            Pair(parts[0].toIntOrNull() ?: 12, parts[1].toIntOrNull() ?: 0)
        } else {
            Pair(12, 0)
        }
    }

    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(label, style = MaterialTheme.typography.labelLarge)
        Spacer(modifier = Modifier.height(8.dp))
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            NumberPicker(
                value = hour,
                onValueChange = { newHour ->
                    onTimeChange("${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}")
                },
                range = 0..23,
                pickerTag = hourPickerTag
            )
            Text(":", style = MaterialTheme.typography.headlineMedium, modifier = Modifier.padding(horizontal = 4.dp))
            NumberPicker(
                value = minute,
                onValueChange = { newMinute ->
                    onTimeChange("${hour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}")
                },
                range = 0..59,
                pickerTag = minutePickerTag
            )
        }
    }
}

// Step 1: Basic Information (same as before)
@Composable
private fun Step1BasicInfo(
    title: String,
    onTitleChange: (String) -> Unit,
    description: String,
    onDescriptionChange: (String) -> Unit,
    mediaUrls: List<String>,
    mobileMediaUrls: List<String>,
    mediaUploadInProgress: Boolean,
    mediaUploadError: String?,
    onMediaAdd: () -> Unit,
    onMediaRemove: (Int) -> Unit,
    mobileMediaUploadInProgress: Boolean,
    mobileMediaUploadError: String?,
    onMobileMediaAdd: () -> Unit,
    onMobileMediaRemove: (Int) -> Unit,
    maxCapacity: String,
    onMaxCapacityChange: (String) -> Unit,
    ticketPrice: String,
    onTicketPriceChange: (String) -> Unit,
    selectedCurrency: Currency,
    onCurrencyClick: () -> Unit,
    hiddenInfo: String,
    onHiddenInfoChange: (String) -> Unit,
    titleError: String?,
    descriptionError: String?,
    ticketPriceError: String?,
    enableGroupChat: Boolean,
    onEnableGroupChatChange: (Boolean) -> Unit,
    useAiLanding: Boolean,
    onUseAiLandingChange: (Boolean) -> Unit,
    landingDescription: String,
    onLandingDescriptionChange: (String) -> Unit,
    enableLandingPage: Boolean,
    onEnableLandingPageChange: (Boolean) -> Unit,
) {
    val strings = strings()
    val aiApi = remember { AiApi() }
    val scope = rememberCoroutineScope()
    var isGeneratingDescription by remember { mutableStateOf(false) }
    var isFormattingDescription by remember { mutableStateOf(false) }
    var isDescriptionTranscribing by remember { mutableStateOf(false) }
    var aiDescriptionError by remember { mutableStateOf<String?>(null) }
    var descriptionTranscriptionError by remember { mutableStateOf<String?>(null) }
    val descriptionVoiceRecorder = rememberVoiceRecorder()

    // AI consent state
    val legalRepository = remember { LegalRepository() }
    var aiConsentGranted by remember { mutableStateOf<Boolean?>(null) }
    var showAiConsentDialog by remember { mutableStateOf(false) }
    var pendingAiAction by remember { mutableStateOf<(suspend () -> Unit)?>(null) }

    LaunchedEffect(Unit) {
        legalRepository.hasAcceptedAiConsent()
            .onSuccess { aiConsentGranted = it }
            .onFailure { aiConsentGranted = false }
    }

    fun requireAiConsent(action: suspend () -> Unit) {
        if (aiConsentGranted == true) {
            scope.launch { action() }
        } else {
            pendingAiAction = action
            showAiConsentDialog = true
        }
    }

    if (showAiConsentDialog) {
        AiConsentDialog(
            onAccepted = {
                aiConsentGranted = true
                showAiConsentDialog = false
                val action = pendingAiAction
                pendingAiAction = null
                if (action != null) {
                    scope.launch { action() }
                }
            },
            onDeclined = {
                showAiConsentDialog = false
                pendingAiAction = null
            },
            legalRepository = legalRepository
        )
    }

    LaunchedEffect(descriptionVoiceRecorder.recordedData, useAiLanding) {
        if (useAiLanding) {
            descriptionVoiceRecorder.clear()
            descriptionTranscriptionError = null
            isDescriptionTranscribing = false
            return@LaunchedEffect
        }
        val data = descriptionVoiceRecorder.recordedData
        if (data != null && data.isNotEmpty()) {
            val duration = descriptionVoiceRecorder.durationSeconds
            when {
                duration < VoiceRecorderState.MIN_DURATION_SECONDS -> {
                    descriptionTranscriptionError = strings.recordingTooShort
                    descriptionVoiceRecorder.clear()
                    return@LaunchedEffect
                }
                duration > VoiceRecorderState.MAX_DURATION_SECONDS -> {
                    descriptionTranscriptionError = strings.recordingTooLong
                    descriptionVoiceRecorder.clear()
                    return@LaunchedEffect
                }
            }

            if (aiConsentGranted != true) {
                val capturedData = data.copyOf()
                val capturedFormat = descriptionVoiceRecorder.audioFormat
                descriptionVoiceRecorder.clear()
                requireAiConsent {
                    isDescriptionTranscribing = true
                    descriptionTranscriptionError = null
                    try {
                        val result = aiApi.transcribeAudio(capturedData, "recording.$capturedFormat")
                        result.fold(
                            onSuccess = { response ->
                                val combined = (description + " " + response.text).trim()
                                onDescriptionChange(combined)
                            },
                            onFailure = { error ->
                                descriptionTranscriptionError = error.message
                            }
                        )
                    } catch (e: Exception) {
                        descriptionTranscriptionError = e.message
                    } finally {
                        isDescriptionTranscribing = false
                    }
                }
                return@LaunchedEffect
            }

            isDescriptionTranscribing = true
            descriptionTranscriptionError = null
            try {
                val result = aiApi.transcribeAudio(data, "recording.${descriptionVoiceRecorder.audioFormat}")
                result.fold(
                    onSuccess = { response ->
                        val combined = (description + " " + response.text).trim()
                        onDescriptionChange(combined)
                    },
                    onFailure = { error ->
                        descriptionTranscriptionError = error.message
                    }
                )
            } catch (e: Exception) {
                descriptionTranscriptionError = e.message
            } finally {
                isDescriptionTranscribing = false
                descriptionVoiceRecorder.clear()
            }
        }
    }

    EventSectionTitle(title = strings.step1BasicInfo)

    // Landing Mode Selector
    LandingModeSelector(
        useAiLanding = useAiLanding,
        onUseAiLandingChange = onUseAiLandingChange
    )

    Spacer(modifier = Modifier.height(16.dp))

    // Event title (always shown above description)
    Text(
        text = strings.eventTitle + " *",
        style = MaterialTheme.typography.labelLarge
    )
    OutlinedTextField(
        value = title,
        onValueChange = {
            aiDescriptionError = null
            onTitleChange(it)
        },
        modifier = Modifier
            .fillMaxWidth()
            .testTag(TestTags.CreateEventWizard.TITLE_FIELD),
        placeholder = { Text(strings.eventTitlePlaceholder) },
        singleLine = true,
        isError = titleError != null,
        supportingText = { titleError?.let { Text(it, color = MaterialTheme.colorScheme.error) } },
        shape = RoundedCornerShape(EventCardInputCorner),
        colors = eventOutlinedFieldColors()
    )

    Spacer(modifier = Modifier.height(8.dp))

    if (useAiLanding) {
        AiLandingDescriptionInput(
            landingDescription = landingDescription,
            onLandingDescriptionChange = onLandingDescriptionChange,
            aiConsentGranted = aiConsentGranted == true,
            onRequestAiConsent = { action -> requireAiConsent(action) }
        )

        // Enable landing page toggle
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 4.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = strings.enableLandingPageLabel,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Medium
                )
                Text(
                    text = strings.enableLandingPageDescription,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Switch(
                checked = enableLandingPage,
                onCheckedChange = onEnableLandingPageChange,
                colors = eventSwitchColors()
            )
        }

        Spacer(modifier = Modifier.height(16.dp))
    } else {
        // Classic description field
        Text(
            text = strings.eventDescription + " *",
            style = MaterialTheme.typography.labelLarge
        )

        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            EventOutlineButton(
                text = strings.aiGenerateDescription,
                onClick = {
                    val trimmedTitle = title.trim()
                    if (trimmedTitle.isEmpty()) {
                        aiDescriptionError = strings.missingTitle
                        return@EventOutlineButton
                    }
                    requireAiConsent {
                        isGeneratingDescription = true
                        aiDescriptionError = null
                        try {
                            val result = aiApi.makeDescription(trimmedTitle)
                            result.fold(
                                onSuccess = { response ->
                                    aiDescriptionError = null
                                    onDescriptionChange(response.description.trim())
                                },
                                onFailure = { error ->
                                    aiDescriptionError = error.message ?: strings.eventSaveErrorMessage
                                }
                            )
                        } catch (e: Exception) {
                            aiDescriptionError = e.message ?: strings.eventSaveErrorMessage
                        } finally {
                            isGeneratingDescription = false
                        }
                    }
                },
                enabled = !isGeneratingDescription && !isFormattingDescription && !isDescriptionTranscribing,
                isLoading = isGeneratingDescription,
                leadingIcon = Icons.Default.AutoFixHigh,
                modifier = Modifier.testTag(TestTags.CreateEventWizard.AI_DESCRIPTION_BUTTON)
            )

            EventOutlineButton(
                text = strings.aiFormatDescription,
                onClick = {
                    val trimmedDraft = description.trim()
                    if (trimmedDraft.isEmpty()) {
                        aiDescriptionError = strings.missingDescription
                        return@EventOutlineButton
                    }
                    requireAiConsent {
                        isFormattingDescription = true
                        aiDescriptionError = null
                        try {
                            val result = aiApi.formatDescription(trimmedDraft)
                            result.fold(
                                onSuccess = { response ->
                                    aiDescriptionError = null
                                    onDescriptionChange(response.description.trim())
                                },
                                onFailure = { error ->
                                    aiDescriptionError = error.message ?: strings.eventSaveErrorMessage
                                }
                            )
                        } catch (e: Exception) {
                            aiDescriptionError = e.message ?: strings.eventSaveErrorMessage
                        } finally {
                            isFormattingDescription = false
                        }
                    }
                },
                enabled = !isFormattingDescription && !isGeneratingDescription && !isDescriptionTranscribing,
                isLoading = isFormattingDescription,
                leadingIcon = Icons.Default.DoneAll,
                modifier = Modifier.testTag(TestTags.CreateEventWizard.AI_FORMAT_DESCRIPTION_BUTTON)
            )
        }

        //Spacer(modifier = Modifier.height(8.dp))

        // Voice recording controls for description
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(
                onClick = {
                    if (descriptionVoiceRecorder.isRecording) {
                        descriptionVoiceRecorder.stopRecording()
                    } else {
                        descriptionTranscriptionError = null
                        descriptionVoiceRecorder.startRecording()
                    }
                },
                enabled = !isDescriptionTranscribing && !isGeneratingDescription && !isFormattingDescription,
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(
                        if (descriptionVoiceRecorder.isRecording) EventCardAlert.copy(alpha = 0.12f)
                        else EventCardAccentSoft
                    )
                    .testTag(TestTags.CreateEventWizard.DESCRIPTION_VOICE_BUTTON)
            ) {
                if (isDescriptionTranscribing) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        strokeWidth = 2.dp,
                        color = EventCardAccent
                    )
                } else {
                    Icon(
                        imageVector = if (descriptionVoiceRecorder.isRecording) Icons.Default.Stop else Icons.Default.Mic,
                        contentDescription = if (descriptionVoiceRecorder.isRecording) strings.stopRecording else strings.recordVoice,
                        tint = if (descriptionVoiceRecorder.isRecording) EventCardAlert else EventCardAccent
                    )
                }
            }

            Text(
                text = when {
                    isDescriptionTranscribing -> strings.transcribing
                    descriptionVoiceRecorder.isRecording -> strings.recordingDuration(descriptionVoiceRecorder.durationSeconds)
                    else -> strings.recordVoice
                },
                style = MaterialTheme.typography.bodyMedium,
                color = when {
                    descriptionVoiceRecorder.isRecording -> EventCardAlert
                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                }
            )
        }

        // Errors from AI description actions
        aiDescriptionError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(top = 4.dp)
            )
        }

        descriptionTranscriptionError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(top = 4.dp)
            )
        }

        OutlinedTextField(
            value = description,
            onValueChange = {
                aiDescriptionError = null
                descriptionTranscriptionError = null
                onDescriptionChange(it)
            },
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.CreateEventWizard.DESCRIPTION_FIELD),
            placeholder = { Text(strings.eventDescriptionPlaceholder) },
            minLines = 4,
            maxLines = 15,
            isError = descriptionError != null,
            supportingText = { descriptionError?.let { Text(it, color = MaterialTheme.colorScheme.error) } },
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )

        Spacer(modifier = Modifier.height(16.dp))
    }

    Text(
        text = strings.addMedia,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.SemiBold
        )
    )

    Text(
        text = strings.mediaDesktopLabel,
        style = MaterialTheme.typography.labelLarge
    )

    EventPrimaryButton(
        text = strings.mediaChooseFile,
        onClick = onMediaAdd,
        enabled = !mediaUploadInProgress,
        isLoading = mediaUploadInProgress,
        leadingIcon = Icons.Default.Image,
        modifier = Modifier
            .fillMaxWidth()
            .height(56.dp)
            .testTag(TestTags.CreateEventWizard.ADD_MEDIA_BUTTON)
    )

    mediaUploadError?.let { error ->
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.padding(top = 4.dp)
        )
    }

    if (mediaUrls.isNotEmpty()) {
        Text(
            text = "${strings.mediaFilesAdded}: ${mediaUrls.size}",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            mediaUrls.forEachIndexed { index, url ->
                EventSurfaceCard(
                    modifier = Modifier.fillMaxWidth(),
                    cornerRadius = EventCardCompactCorner,
                    contentPadding = PaddingValues(12.dp),
                    shadowElevation = 1.dp
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = url.take(40) + if (url.length > 40) "..." else "",
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodySmall
                        )
                        IconButton(onClick = { onMediaRemove(index) }) {
                            Icon(Icons.Default.Close, contentDescription = strings.delete)
                        }
                    }
                }
            }
        }
    }

    Spacer(modifier = Modifier.height(12.dp))

    Text(
        text = strings.mediaMobileLabel,
        style = MaterialTheme.typography.labelLarge
    )

    EventPrimaryButton(
        text = strings.mediaChooseFile,
        onClick = onMobileMediaAdd,
        enabled = !mobileMediaUploadInProgress,
        isLoading = mobileMediaUploadInProgress,
        leadingIcon = Icons.Default.Image,
        modifier = Modifier
            .fillMaxWidth()
            .height(56.dp)
    )

    mobileMediaUploadError?.let { error ->
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.padding(top = 4.dp)
        )
    }

    if (mobileMediaUrls.isNotEmpty()) {
        Text(
            text = "${strings.mediaFilesAdded}: ${mobileMediaUrls.size}",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            mobileMediaUrls.forEachIndexed { index, url ->
                EventSurfaceCard(
                    modifier = Modifier.fillMaxWidth(),
                    cornerRadius = EventCardCompactCorner,
                    contentPadding = PaddingValues(12.dp),
                    shadowElevation = 1.dp
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = url.take(40) + if (url.length > 40) "..." else "",
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodySmall
                        )
                        IconButton(onClick = { onMobileMediaRemove(index) }) {
                            Icon(Icons.Default.Close, contentDescription = strings.delete)
                        }
                    }
                }
            }
        }
    }

    Spacer(modifier = Modifier.height(16.dp))

    Text(
        text = strings.maxCapacity,
        style = MaterialTheme.typography.labelLarge
    )
    OutlinedTextField(
        value = maxCapacity,
        onValueChange = onMaxCapacityChange,
        placeholder = { Text(strings.maxCapacityPlaceholder) },
        modifier = Modifier
            .fillMaxWidth()
            .testTag(TestTags.CreateEventWizard.MAX_CAPACITY_FIELD),
        keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
            keyboardType = KeyboardType.Number
        ),
        singleLine = true,
        shape = RoundedCornerShape(EventCardInputCorner),
        colors = eventOutlinedFieldColors()
    )

    Spacer(modifier = Modifier.height(12.dp))

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Column(modifier = Modifier.weight(2f)) {
            Text(
                text = "${strings.ticketPrice} (${selectedCurrency.symbol})",
                style = MaterialTheme.typography.labelLarge
            )
            OutlinedTextField(
                value = ticketPrice,
                onValueChange = onTicketPriceChange,
                placeholder = { Text(strings.ticketPricePlaceholder) },
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.CreateEventWizard.TICKET_PRICE_FIELD),
                keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                    keyboardType = KeyboardType.Number
                ),
                singleLine = true,
                isError = ticketPriceError != null,
                supportingText = { ticketPriceError?.let { Text(it, color = MaterialTheme.colorScheme.error) } },
                shape = RoundedCornerShape(EventCardInputCorner),
                colors = eventOutlinedFieldColors()
            )
        }

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = strings.currency,
                style = MaterialTheme.typography.labelLarge
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable(onClick = onCurrencyClick)
            ) {
                OutlinedTextField(
                    value = selectedCurrency.code,
                    onValueChange = {},
                    modifier = Modifier.fillMaxWidth(),
                    enabled = false,
                    trailingIcon = {
                        Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                    },
                    singleLine = true,
                    colors = eventOutlinedFieldColors(),
                    shape = RoundedCornerShape(EventCardInputCorner)
                )
            }
        }
    }

    Spacer(modifier = Modifier.height(16.dp))

    Text(
        text = strings.hiddenInfo,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.SemiBold
        )
    )
    Text(
        text = strings.hiddenInfoDescription,
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
    OutlinedTextField(
        value = hiddenInfo,
        onValueChange = onHiddenInfoChange,
        modifier = Modifier
            .fillMaxWidth()
            .height(100.dp),
        placeholder = { Text(strings.hiddenInfoPlaceholder) },
        leadingIcon = {
            Icon(Icons.Default.Lock, contentDescription = null)
        },
        maxLines = 4,
        shape = RoundedCornerShape(EventCardInputCorner),
        colors = eventOutlinedFieldColors()
    )

    Spacer(modifier = Modifier.height(16.dp))

    EventToggleCard(
        title = strings.enableGroupChatLabel,
        description = strings.enableGroupChatDescription,
        checked = enableGroupChat,
        onCheckedChange = onEnableGroupChatChange,
        modifier = Modifier.fillMaxWidth(),
        switchModifier = Modifier.testTag(TestTags.CreateEventWizard.GROUP_CHAT_SWITCH)
    )
}

// Step 2: Schedule with Wheel Pickers
@Composable
private fun Step2Schedule(
    scheduleType: ScheduleType?,
    onScheduleTypeChange: (ScheduleType) -> Unit,
    eventDate: LocalDate?,
    onEventDateChange: (LocalDate) -> Unit,
    eventEndDate: LocalDate?,
    onEventEndDateChange: (LocalDate) -> Unit,
    eventTime: String,
    onEventTimeChange: (String) -> Unit,
    eventEndTime: String,
    onEventEndTimeChange: (String) -> Unit,
    selectedTimeZone: TimeZone,
    selectedTimeZoneLabel: String,
    onTimeZoneClick: () -> Unit,
    selectedDaysOfWeek: Set<Int>,
    onDaysOfWeekChange: (Set<Int>) -> Unit,
    weeklyTime: String,
    onWeeklyTimeChange: (String) -> Unit,
    weeklyEndTime: String,
    onWeeklyEndTimeChange: (String) -> Unit,
    selectedDaysOfMonth: Set<Int>,
    onDaysOfMonthChange: (Set<Int>) -> Unit,
    monthlyTime: String,
    onMonthlyTimeChange: (String) -> Unit,
    slotDaysOfWeek: Set<Int>,
    onSlotDaysOfWeekChange: (Set<Int>) -> Unit,
    slotStartTime: String,
    onSlotStartTimeChange: (String) -> Unit,
    slotEndTime: String,
    onSlotEndTimeChange: (String) -> Unit,
    minSlotsPerBooking: String,
    onMinSlotsPerBookingChange: (String) -> Unit,
    slotDurationMinutes: String,
    onSlotDurationMinutesChange: (String) -> Unit,
    slotDayTimes: Map<Int, Pair<String, String>>,
    onSlotDayTimesChange: (Map<Int, Pair<String, String>>) -> Unit
) {
    val strings = strings()

    EventSectionTitle(title = strings.step2Schedule)

    Text(
        text = strings.scheduleTypeLabel,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.SemiBold
        )
    )

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        ScheduleTypeCard(
            title = strings.oneTimeEvent,
            description = strings.scheduleOneTimeDescription,
            selected = scheduleType == ScheduleType.ONE_TIME,
            onClick = { onScheduleTypeChange(ScheduleType.ONE_TIME) },
            modifier = Modifier.testTag(TestTags.CreateEventWizard.ONE_TIME_OPTION)
        )
        ScheduleTypeCard(
            title = strings.weekly,
            description = strings.scheduleWeeklyDescription,
            selected = scheduleType == ScheduleType.WEEKLY,
            onClick = { onScheduleTypeChange(ScheduleType.WEEKLY) },
            modifier = Modifier.testTag(TestTags.CreateEventWizard.WEEKLY_OPTION)
        )
        ScheduleTypeCard(
            title = strings.slotBooking,
            description = strings.scheduleSlotDescription,
            selected = scheduleType == ScheduleType.SLOT_BOOKING,
            onClick = { onScheduleTypeChange(ScheduleType.SLOT_BOOKING) },
            modifier = Modifier.testTag(TestTags.CreateEventWizard.SLOT_OPTION)
        )
    }

    Spacer(modifier = Modifier.height(16.dp))

    when (scheduleType) {
        ScheduleType.ONE_TIME -> {
            EventInfoCard(
                title = strings.scheduleOneTimeSetup,
                icon = Icons.Default.Settings,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(12.dp))

            Text(strings.selectDate, style = MaterialTheme.typography.labelLarge)
            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth(),
                cornerRadius = EventCardCompactCorner,
                contentPadding = PaddingValues(0.dp),
                verticalArrangement = Arrangement.Top
            ) {
                WheelDatePicker(
                    selectedDate = eventDate ?: getCurrentDate(selectedTimeZone),
                    onDateChange = onEventDateChange,
                    modifier = Modifier.padding(16.dp),
                    strings = strings,
                    dayPickerTag = TestTags.CreateEventWizard.ONE_TIME_DAY_PICKER,
                    monthPickerTag = TestTags.CreateEventWizard.ONE_TIME_MONTH_PICKER,
                    yearPickerTag = TestTags.CreateEventWizard.ONE_TIME_YEAR_PICKER
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            Text(strings.endDateLabel, style = MaterialTheme.typography.labelLarge)
            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth(),
                cornerRadius = EventCardCompactCorner,
                contentPadding = PaddingValues(0.dp),
                verticalArrangement = Arrangement.Top
            ) {
                val endPickerDate = eventEndDate ?: eventDate ?: getCurrentDate(selectedTimeZone)
                WheelDatePicker(
                    selectedDate = endPickerDate,
                    onDateChange = { picked ->
                        val start = eventDate ?: picked
                        val resolved = if (picked < start) start else picked
                        onEventEndDateChange(resolved)
                    },
                    modifier = Modifier.padding(16.dp),
                    strings = strings,
                    dayPickerTag = TestTags.CreateEventWizard.ONE_TIME_END_DAY_PICKER,
                    monthPickerTag = TestTags.CreateEventWizard.ONE_TIME_END_MONTH_PICKER,
                    yearPickerTag = TestTags.CreateEventWizard.ONE_TIME_END_YEAR_PICKER
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth(),
                cornerRadius = EventCardCompactCorner,
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.Top
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly
                ) {
                    WheelTimePicker(
                        time = eventTime.ifEmpty { "12:00" },
                        onTimeChange = { newStartTime ->
                            onEventTimeChange(newStartTime)
                            // Auto-adjust end time if start time becomes later than end time
                            val currentEndTime = eventEndTime.ifEmpty { "14:00" }
                            if (!isStartTimeBeforeEndTime(newStartTime, currentEndTime)) {
                                onEventEndTimeChange(addMinutesToTime(newStartTime, 60))
                            }
                        },
                        label = strings.startTime,
                        hourPickerTag = TestTags.CreateEventWizard.ONE_TIME_START_HOUR_PICKER,
                        minutePickerTag = TestTags.CreateEventWizard.ONE_TIME_START_MINUTE_PICKER
                    )
                    WheelTimePicker(
                        time = eventEndTime.ifEmpty { "14:00" },
                        onTimeChange = { newEndTime ->
                            val currentStartTime = eventTime.ifEmpty { "12:00" }
                            // Only allow end time if it's after start time
                            if (isStartTimeBeforeEndTime(currentStartTime, newEndTime)) {
                                onEventEndTimeChange(newEndTime)
                            } else {
                                // Set end time to start time + 1 hour
                                onEventEndTimeChange(addMinutesToTime(currentStartTime, 60))
                            }
                        },
                        label = strings.endTime,
                        hourPickerTag = TestTags.CreateEventWizard.ONE_TIME_END_HOUR_PICKER,
                        minutePickerTag = TestTags.CreateEventWizard.ONE_TIME_END_MINUTE_PICKER
                    )
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            Text(strings.timeZone, style = MaterialTheme.typography.labelLarge)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.CreateEventWizard.TIME_ZONE_FIELD)
                    .clickable(onClick = onTimeZoneClick)
            ) {
                OutlinedTextField(
                    value = selectedTimeZoneLabel,
                    onValueChange = {},
                    modifier = Modifier.fillMaxWidth(),
                    enabled = false,
                    trailingIcon = {
                        Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                    },
                    colors = eventOutlinedFieldColors(),
                    shape = RoundedCornerShape(EventCardInputCorner)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            EventInfoCard(
                title = strings.schedulePreviewTitle,
                icon = Icons.Default.Visibility,
                modifier = Modifier.fillMaxWidth()
            ) {
                Divider(color = EventCardOutline)
                val previewDate = eventDate?.toString() ?: strings.notSelected
                val previewStart = eventTime.ifEmpty { strings.notSelected }
                val previewEnd = eventEndTime.ifEmpty { strings.notSelected }
                Text(strings.schedulePreviewDate(previewDate), style = MaterialTheme.typography.bodySmall)
                Text(
                    strings.schedulePreviewTime(previewStart, previewEnd),
                    style = MaterialTheme.typography.bodySmall
                )
                Text(strings.timeZoneValue(selectedTimeZoneLabel), style = MaterialTheme.typography.bodySmall)
            }
        }

        ScheduleType.WEEKLY -> {
            Text(strings.selectDaysOfWeek, style = MaterialTheme.typography.labelLarge)
            val daysOfWeek = (0 until 7).map(strings::weekdayShortName)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                daysOfWeek.forEachIndexed { index, day ->
                    val isSelected = selectedDaysOfWeek.contains(index)
                    FilterChip(
                        selected = isSelected,
                        onClick = {
                            val newSet = if (isSelected) {
                                selectedDaysOfWeek - index
                            } else {
                                selectedDaysOfWeek + index
                            }
                            onDaysOfWeekChange(newSet)
                        },
                        label = {
                            Text(
                                day,
                                style = MaterialTheme.typography.labelSmall,
                                maxLines = 1
                            )
                        },
                        modifier = Modifier
                            .weight(1f)
                            .testTag("${TestTags.CreateEventWizard.WEEKLY_DAY_PREFIX}$index"),
                        colors = eventFilterChipColors(),
                        border = eventFilterChipBorder(isSelected)
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth(),
                cornerRadius = EventCardCompactCorner,
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.Top
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly
                ) {
                    WheelTimePicker(
                        time = weeklyTime.ifEmpty { "12:00" },
                        onTimeChange = { newStartTime ->
                            onWeeklyTimeChange(newStartTime)
                            // Auto-adjust end time if start time becomes later than end time
                            val currentEndTime = weeklyEndTime.ifEmpty { "14:00" }
                            if (!isStartTimeBeforeEndTime(newStartTime, currentEndTime)) {
                                onWeeklyEndTimeChange(addMinutesToTime(newStartTime, 60))
                            }
                        },
                        label = strings.startTime,
                        hourPickerTag = TestTags.CreateEventWizard.WEEKLY_START_HOUR_PICKER,
                        minutePickerTag = TestTags.CreateEventWizard.WEEKLY_START_MINUTE_PICKER
                    )
                    WheelTimePicker(
                        time = weeklyEndTime.ifEmpty { "14:00" },
                        onTimeChange = { newEndTime ->
                            val currentStartTime = weeklyTime.ifEmpty { "12:00" }
                            // Only allow end time if it's after start time
                            if (isStartTimeBeforeEndTime(currentStartTime, newEndTime)) {
                                onWeeklyEndTimeChange(newEndTime)
                            } else {
                                // Set end time to start time + 1 hour
                                onWeeklyEndTimeChange(addMinutesToTime(currentStartTime, 60))
                            }
                        },
                        label = strings.endTime,
                        hourPickerTag = TestTags.CreateEventWizard.WEEKLY_END_HOUR_PICKER,
                        minutePickerTag = TestTags.CreateEventWizard.WEEKLY_END_MINUTE_PICKER
                    )
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            Text(strings.timeZone, style = MaterialTheme.typography.labelLarge)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.CreateEventWizard.TIME_ZONE_FIELD)
                    .clickable(onClick = onTimeZoneClick)
            ) {
                OutlinedTextField(
                    value = selectedTimeZoneLabel,
                    onValueChange = {},
                    modifier = Modifier.fillMaxWidth(),
                    enabled = false,
                    trailingIcon = {
                        Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                    },
                    colors = eventOutlinedFieldColors(),
                    shape = RoundedCornerShape(EventCardInputCorner)
                )
            }
        }

        ScheduleType.SLOT_BOOKING -> {
            // Slot configuration fields
            Text(strings.slotSettings, style = MaterialTheme.typography.labelLarge)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedTextField(
                    value = minSlotsPerBooking,
                    onValueChange = { value -> onMinSlotsPerBookingChange(value.filter { c -> c.isDigit() }) },
                    label = { Text(strings.minSlots) },
                    placeholder = { Text(strings.minSlotsPlaceholder) },
                    keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                        keyboardType = KeyboardType.Number
                    ),
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                OutlinedTextField(
                    value = slotDurationMinutes,
                    onValueChange = { value -> onSlotDurationMinutesChange(value.filter { c -> c.isDigit() }) },
                    label = { Text(strings.slotDuration) },
                    placeholder = { Text(strings.slotDurationPlaceholder) },
                    keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                        keyboardType = KeyboardType.Number
                    ),
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            Text(strings.selectDaysOfWeek, style = MaterialTheme.typography.labelLarge)
            val daysOfWeek = (0 until 7).map(strings::weekdayShortName)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                daysOfWeek.forEachIndexed { index, day ->
                    val isSelected = slotDaysOfWeek.contains(index)
                    FilterChip(
                        selected = isSelected,
                        onClick = {
                            val newSet = if (isSelected) {
                                slotDaysOfWeek - index
                            } else {
                                slotDaysOfWeek + index
                            }
                            onSlotDaysOfWeekChange(newSet)
                            onSlotDayTimesChange(
                                when {
                                    // Remove stale per-day configuration when day is deselected
                                    isSelected -> slotDayTimes - index
                                    // Initialize default times for newly selected day
                                    !slotDayTimes.containsKey(index) -> slotDayTimes + (index to ("09:00" to "18:00"))
                                    else -> slotDayTimes
                                }
                            )
                        },
                        label = {
                            Text(
                                day,
                                style = MaterialTheme.typography.labelSmall,
                                maxLines = 1
                            )
                        },
                        modifier = Modifier
                            .weight(1f)
                            .testTag("${TestTags.CreateEventWizard.SLOT_DAY_PREFIX}$index"),
                        colors = eventFilterChipColors(),
                        border = eventFilterChipBorder(isSelected)
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Per-day time configuration
            if (slotDaysOfWeek.isNotEmpty()) {
                Text(strings.perDayTimeConfiguration, style = MaterialTheme.typography.labelLarge)
                Spacer(modifier = Modifier.height(8.dp))

                slotDaysOfWeek.sorted().forEach { dayIndex ->
                    val dayName = strings.weekdayName(dayIndex)
                    val times = slotDayTimes[dayIndex] ?: ("09:00" to "18:00")

                    EventSurfaceCard(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 4.dp),
                        cornerRadius = EventCardCompactCorner,
                        contentPadding = PaddingValues(12.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = dayName,
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceEvenly
                        ) {
                            WheelTimePicker(
                                time = times.first,
                                onTimeChange = { newStartTime ->
                                    val currentEndTime = times.second
                                    // Auto-adjust end time if start time becomes later than end time
                                    if (!isStartTimeBeforeEndTime(newStartTime, currentEndTime)) {
                                        val newEndTime = addMinutesToTime(newStartTime, 60)
                                        onSlotDayTimesChange(slotDayTimes + (dayIndex to (newStartTime to newEndTime)))
                                    } else {
                                        onSlotDayTimesChange(slotDayTimes + (dayIndex to (newStartTime to currentEndTime)))
                                    }
                                },
                                label = strings.startTime,
                                hourPickerTag = "${TestTags.CreateEventWizard.SLOT_DAY_START_HOUR_PREFIX}$dayIndex",
                                minutePickerTag = "${TestTags.CreateEventWizard.SLOT_DAY_START_MINUTE_PREFIX}$dayIndex"
                            )
                            WheelTimePicker(
                                time = times.second,
                                onTimeChange = { newEndTime ->
                                    val currentStartTime = times.first
                                    // Only allow end time if it's after start time
                                    if (isStartTimeBeforeEndTime(currentStartTime, newEndTime)) {
                                        onSlotDayTimesChange(slotDayTimes + (dayIndex to (currentStartTime to newEndTime)))
                                    } else {
                                        // Set end time to start time + 1 hour
                                        val adjustedEndTime = addMinutesToTime(currentStartTime, 60)
                                        onSlotDayTimesChange(slotDayTimes + (dayIndex to (currentStartTime to adjustedEndTime)))
                                    }
                                },
                                label = strings.endTime,
                                hourPickerTag = "${TestTags.CreateEventWizard.SLOT_DAY_END_HOUR_PREFIX}$dayIndex",
                                minutePickerTag = "${TestTags.CreateEventWizard.SLOT_DAY_END_MINUTE_PREFIX}$dayIndex"
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            Text(strings.timeZone, style = MaterialTheme.typography.labelLarge)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.CreateEventWizard.TIME_ZONE_FIELD)
                    .clickable(onClick = onTimeZoneClick)
            ) {
                OutlinedTextField(
                    value = selectedTimeZoneLabel,
                    onValueChange = {},
                    modifier = Modifier.fillMaxWidth(),
                    enabled = false,
                    trailingIcon = {
                        Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                    },
                    colors = eventOutlinedFieldColors(),
                    shape = RoundedCornerShape(EventCardInputCorner)
                )
            }
        }

        ScheduleType.MONTHLY -> {
            Text(strings.selectDaysOfMonth, style = MaterialTheme.typography.labelLarge)
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                (1..31).chunked(7).forEach { weekDays ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        weekDays.forEach { dayNumber ->
                            val isSelected = selectedDaysOfMonth.contains(dayNumber)
                            FilterChip(
                                selected = isSelected,
                                onClick = {
                                    val newSet = if (isSelected) {
                                        selectedDaysOfMonth - dayNumber
                                    } else {
                                        selectedDaysOfMonth + dayNumber
                                    }
                                    onDaysOfMonthChange(newSet)
                                },
                                label = { Text(dayNumber.toString()) },
                                modifier = Modifier.weight(1f),
                                colors = eventFilterChipColors(),
                                border = eventFilterChipBorder(isSelected)
                            )
                        }
                        repeat(7 - weekDays.size) {
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
            OutlinedTextField(
                value = monthlyTime,
                onValueChange = onMonthlyTimeChange,
                label = { Text(strings.time) },
                modifier = Modifier.fillMaxWidth(),
                placeholder = { Text("19:00") },
                shape = RoundedCornerShape(EventCardInputCorner),
                colors = eventOutlinedFieldColors()
            )
        }

        null -> {}
    }
}

@Composable
private fun ScheduleTypeCard(
    title: String,
    description: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    EventSelectableCard(
        selected = selected,
        onClick = onClick,
        modifier = modifier.fillMaxWidth()
    ) {
        RadioButton(
            selected = selected,
            onClick = onClick,
            colors = RadioButtonDefaults.colors(
                selectedColor = EventCardAccent
            )
        )
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyLarge.copy(
                    fontWeight = FontWeight.SemiBold
                )
            )
            Text(
                text = description,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

// Step 3: Event Setup (same as before)
@Composable
private fun Step3EventSetup(
    dressCode: String,
    onDressCodeChange: (String) -> Unit,
    locationAddress: String,
    onLocationAddressChange: (String) -> Unit,
    onSelectOnMapClick: () -> Unit,
    enableCarpool: Boolean,
    onEnableCarpoolChange: (Boolean) -> Unit,
    carpoolPaymentPerPerson: String,
    onCarpoolPaymentChange: (String) -> Unit,
    currency: Currency,
    requireContactInfo: Boolean,
    onRequireContactInfoChange: (Boolean) -> Unit
) {
    val strings = strings()

    EventSectionTitle(title = strings.eventSetupTitle)

    Text(
        text = strings.dressCode,
        style = MaterialTheme.typography.labelLarge
    )
    Box(
        modifier = Modifier.fillMaxWidth()
    ) {
        OutlinedTextField(
            value = dressCode,
            onValueChange = onDressCodeChange,
            placeholder = { Text(strings.dressCodePlaceholder) },
            modifier = Modifier.fillMaxWidth(),
            trailingIcon = {
                Icon(Icons.Default.ArrowDropDown, contentDescription = null)
            },
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )
    }

    Spacer(modifier = Modifier.height(16.dp))

    Text(
        text = strings.location,
        style = MaterialTheme.typography.labelLarge
    )
    OutlinedTextField(
        value = locationAddress,
        onValueChange = onLocationAddressChange,
        placeholder = { Text(strings.locationSearchPlaceholder) },
        modifier = Modifier
            .fillMaxWidth()
            .testTag(TestTags.CreateEventWizard.LOCATION_ADDRESS_FIELD),
        leadingIcon = {
            Icon(Icons.Default.LocationOn, contentDescription = null)
        },
        trailingIcon = {
            Icon(Icons.Default.Search, contentDescription = null)
        },
        singleLine = true,
        shape = RoundedCornerShape(EventCardInputCorner),
        colors = eventOutlinedFieldColors()
    )

    Spacer(modifier = Modifier.height(16.dp))

    // Carpool settings
    Text(
        text = strings.carpoolSettings,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.SemiBold
        )
    )

    EventToggleCard(
        title = strings.enableCarpool,
        description = strings.carpoolDescription,
        checked = enableCarpool,
        onCheckedChange = onEnableCarpoolChange,
        modifier = Modifier.fillMaxWidth()
    )

    if (enableCarpool) {
        Spacer(modifier = Modifier.height(12.dp))

        Text(
            text = strings.carpoolPaymentLabel(currency.symbol),
            style = MaterialTheme.typography.labelLarge
        )
        OutlinedTextField(
            value = carpoolPaymentPerPerson,
            onValueChange = onCarpoolPaymentChange,
            placeholder = { Text(strings.carpoolPaymentPlaceholder) },
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                keyboardType = KeyboardType.Number
            ),
            leadingIcon = {
                Icon(Icons.Default.DirectionsCar, contentDescription = null)
            },
            singleLine = true,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )

        Text(
            text = strings.carpoolPaymentHint,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(top = 4.dp)
        )
    }

    Spacer(modifier = Modifier.height(16.dp))

    // Contact info collection settings
    Text(
        text = strings.contactInfoSettings,
        style = MaterialTheme.typography.titleMedium.copy(
            fontWeight = FontWeight.SemiBold
        )
    )

    EventToggleCard(
        title = strings.requireContactInfo,
        description = strings.requireContactInfoDescription,
        checked = requireContactInfo,
        onCheckedChange = onRequireContactInfoChange,
        modifier = Modifier.fillMaxWidth()
    )
}

// Dialogs
@Composable
private fun TimeZoneSelectionDialog(
    currentTimeZoneId: String,
    onTimeZoneSelected: (String) -> Unit,
    onDismiss: () -> Unit
) {
    val strings = strings()
    val timeZones = remember { getAllTimeZones() }
    var searchQuery by remember { mutableStateOf("") }
    val filteredTimeZones = remember(searchQuery, timeZones) {
        if (searchQuery.isEmpty()) {
            timeZones
        } else {
            timeZones.filter { it.contains(searchQuery, ignoreCase = true) }
        }
    }

    EventDialog(onDismissRequest = onDismiss) {
        Text(
            text = strings.selectTimeZone,
            style = MaterialTheme.typography.titleMedium,
            color = EventCardAccentDark
        )
        OutlinedTextField(
            value = searchQuery,
            onValueChange = { searchQuery = it },
            label = { Text(strings.search) },
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.CreateEventWizard.TIME_ZONE_SEARCH_FIELD),
            singleLine = true,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .height(360.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            filteredTimeZones.forEach { tzId ->
                val onSelect = {
                    onTimeZoneSelected(tzId)
                    onDismiss()
                }
                EventSelectableCard(
                    selected = currentTimeZoneId == tzId,
                    onClick = onSelect,
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag("${TestTags.CreateEventWizard.TIME_ZONE_ITEM_PREFIX}$tzId"),
                    contentPadding = PaddingValues(12.dp)
                ) {
                    RadioButton(
                        selected = currentTimeZoneId == tzId,
                        onClick = onSelect,
                        colors = RadioButtonDefaults.colors(
                            selectedColor = EventCardAccent
                        )
                    )
                    Text(
                        text = tzId,
                        modifier = Modifier
                            .padding(start = 8.dp)
                            .weight(1f),
                        color = if (currentTimeZoneId == tzId) {
                            EventCardAccentDark
                        } else {
                            MaterialTheme.colorScheme.onSurface
                        }
                    )
                }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End
        ) {
            EventTextAction(
                text = strings.ok,
                onClick = onDismiss
            )
        }
    }
}

@Composable
private fun CurrencySelectionDialog(
    currentCurrency: Currency,
    onCurrencySelected: (Currency) -> Unit,
    onDismiss: () -> Unit
) {
    val strings = strings()
    val currencies = remember { getAllCurrencies() }
    var searchQuery by remember { mutableStateOf("") }
    val filteredCurrencies = remember(searchQuery, currencies) {
        if (searchQuery.isEmpty()) {
            currencies
        } else {
            currencies.filter {
                it.code.contains(searchQuery, ignoreCase = true) ||
                it.name.contains(searchQuery, ignoreCase = true)
            }
        }
    }

    EventDialog(onDismissRequest = onDismiss) {
        Text(
            text = strings.selectCurrency,
            style = MaterialTheme.typography.titleMedium,
            color = EventCardAccentDark
        )
        OutlinedTextField(
            value = searchQuery,
            onValueChange = { searchQuery = it },
            label = { Text(strings.search) },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .height(360.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            filteredCurrencies.forEach { currency ->
                val onSelect = {
                    onCurrencySelected(currency)
                    onDismiss()
                }
                EventSelectableCard(
                    selected = currentCurrency.code == currency.code,
                    onClick = onSelect,
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(12.dp)
                ) {
                    RadioButton(
                        selected = currentCurrency.code == currency.code,
                        onClick = onSelect,
                        colors = RadioButtonDefaults.colors(
                            selectedColor = EventCardAccent
                        )
                    )
                    Text(
                        "${currency.code} (${currency.symbol}) - ${currency.name}",
                        modifier = Modifier.padding(start = 8.dp),
                        color = if (currentCurrency.code == currency.code) {
                            EventCardAccentDark
                        } else {
                            MaterialTheme.colorScheme.onSurface
                        }
                    )
                }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End
        ) {
            EventTextAction(
                text = strings.ok,
                onClick = onDismiss
            )
        }
    }
}

enum class ScheduleType {
    ONE_TIME,
    WEEKLY,
    MONTHLY,
    SLOT_BOOKING
}

data class EventWizardData(
    val title: String,
    val description: String,
    val mediaUrls: List<String>,
    val mobileImageUrls: List<String> = emptyList(),
    val mediaStorageFolder: String? = null,
    val status: String? = null,
    val maxCapacity: Int?,
    val ticketPrice: Double,
    val currency: String,
    val hiddenInfo: String,
    val scheduleType: ScheduleType?,
    val eventDate: LocalDate?,
    val eventEndDate: LocalDate? = null,
    val eventTime: String,
    val eventEndTime: String = "",
    val timeZone: String,
    val selectedDaysOfWeek: Set<Int>,
    val weeklyTime: String,
    val weeklyEndTime: String = "",
    val selectedDaysOfMonth: Set<Int>,
    val monthlyTime: String,
    val slotDaysOfWeek: Set<Int>,
    val slotStartTime: String,
    val slotEndTime: String,
    val minSlotsPerBooking: Int?,
    val slotDurationMinutes: Int?,
    val slotDayTimes: Map<Int, Pair<String, String>>,
    val isOnline: Boolean,
    val locationAddress: String,
    val locationLatitude: Double?,
    val locationLongitude: Double?,
    val onlineLink: String,
    val enableCarpool: Boolean,
    val carpoolPaymentPerPerson: Double?,
    val enableGroupChat: Boolean = false,
    val requireContactInfo: Boolean = false,
    val useAiLanding: Boolean = false,
    val landingDescription: String = "",
    val enableLandingPage: Boolean = false
)

private data class Tuple4<A, B, C, D>(val first: A, val second: B, val third: C, val fourth: D)

// AI Landing Mode Components

@Composable
private fun LandingModeSelector(
    useAiLanding: Boolean,
    onUseAiLandingChange: (Boolean) -> Unit
) {
    val strings = strings()

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = strings.landingModeTitle,
            style = MaterialTheme.typography.titleMedium.copy(
                fontWeight = FontWeight.SemiBold
            )
        )

        Spacer(modifier = Modifier.height(8.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Classic mode button
            EventSegmentButton(
                text = strings.landingModeClassic,
                selected = !useAiLanding,
                onClick = { onUseAiLandingChange(false) },
                modifier = Modifier.weight(1f)
            )

            // AI mode button
            EventSegmentButton(
                text = strings.landingModeAi,
                selected = useAiLanding,
                onClick = { onUseAiLandingChange(true) },
                modifier = Modifier.weight(1f),
                leadingIcon = Icons.Default.AutoAwesome
            )
        }
    }
}

@Composable
private fun AiLandingDescriptionInput(
    landingDescription: String,
    onLandingDescriptionChange: (String) -> Unit,
    aiConsentGranted: Boolean = true,
    onRequestAiConsent: (suspend () -> Unit) -> Unit = { }
) {
    val strings = strings()
    val voiceRecorder = rememberVoiceRecorder()
    val aiApi = remember { com.ijoin.data.api.AiApi() }

    var isTranscribing by remember { mutableStateOf(false) }
    var transcriptionError by remember { mutableStateOf<String?>(null) }

    // Handle transcription when recording stops and we have data
    LaunchedEffect(voiceRecorder.recordedData) {
        val data = voiceRecorder.recordedData
        if (data != null && data.isNotEmpty()) {
            // Validate duration
            val duration = voiceRecorder.durationSeconds
            when {
                duration < VoiceRecorderState.MIN_DURATION_SECONDS -> {
                    transcriptionError = strings.recordingTooShort
                    voiceRecorder.clear()
                    return@LaunchedEffect
                }
                duration > VoiceRecorderState.MAX_DURATION_SECONDS -> {
                    transcriptionError = strings.recordingTooLong
                    voiceRecorder.clear()
                    return@LaunchedEffect
                }
            }

            if (!aiConsentGranted) {
                val capturedData = data.copyOf()
                val capturedFormat = voiceRecorder.audioFormat
                voiceRecorder.clear()
                onRequestAiConsent {
                    isTranscribing = true
                    transcriptionError = null
                    try {
                        val result = aiApi.transcribeAudio(capturedData, "recording.$capturedFormat")
                        result.fold(
                            onSuccess = { response ->
                                onLandingDescriptionChange(landingDescription + " " + response.text)
                            },
                            onFailure = { error ->
                                transcriptionError = error.message
                            }
                        )
                    } catch (e: Exception) {
                        transcriptionError = e.message
                    } finally {
                        isTranscribing = false
                    }
                }
                return@LaunchedEffect
            }

            // Transcribe
            isTranscribing = true
            transcriptionError = null
            try {
                val result = aiApi.transcribeAudio(data, "recording.${voiceRecorder.audioFormat}")
                result.fold(
                    onSuccess = { response ->
                        onLandingDescriptionChange(landingDescription + " " + response.text)
                    },
                    onFailure = { error ->
                        transcriptionError = error.message
                    }
                )
            } catch (e: Exception) {
                transcriptionError = e.message
            } finally {
                isTranscribing = false
                voiceRecorder.clear()
            }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = strings.landingDescriptionLabel,
            style = MaterialTheme.typography.labelLarge
        )

        Spacer(modifier = Modifier.height(8.dp))

        // Voice recording button and status
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Microphone button
            IconButton(
                onClick = {
                    if (voiceRecorder.isRecording) {
                        voiceRecorder.stopRecording()
                    } else {
                        transcriptionError = null
                        voiceRecorder.startRecording()
                    }
                },
                enabled = !isTranscribing,
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(
                        if (voiceRecorder.isRecording) EventCardAlert.copy(alpha = 0.12f)
                        else EventCardAccentSoft
                    )
            ) {
                if (isTranscribing) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        strokeWidth = 2.dp,
                        color = EventCardAccent
                    )
                } else {
                    Icon(
                        imageVector = if (voiceRecorder.isRecording) Icons.Default.Stop else Icons.Default.Mic,
                        contentDescription = if (voiceRecorder.isRecording) strings.stopRecording else strings.recordVoice,
                        tint = if (voiceRecorder.isRecording) EventCardAlert else EventCardAccent
                    )
                }
            }

            // Status text
            Text(
                text = when {
                    isTranscribing -> strings.transcribing
                    voiceRecorder.isRecording -> strings.recordingDuration(voiceRecorder.durationSeconds)
                    else -> strings.recordVoice
                },
                style = MaterialTheme.typography.bodyMedium,
                color = when {
                    voiceRecorder.isRecording -> EventCardAlert
                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                }
            )
        }

        // Error messages
        voiceRecorder.error?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(top = 4.dp)
            )
        }

        transcriptionError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(top = 4.dp)
            )
        }

        Spacer(modifier = Modifier.height(8.dp))

        // Text input for landing description
        OutlinedTextField(
            value = landingDescription,
            onValueChange = onLandingDescriptionChange,
            modifier = Modifier
                .fillMaxWidth()
                .height(150.dp),
            placeholder = { Text(strings.landingDescriptionPlaceholder) },
            maxLines = 8,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors()
        )
    }
}
